import { isActiveInMonth, toDate } from './dates.js';

export type SegmentKind = 'initial' | 'revision';

export interface SegmentLike {
  id: string;
  lineItemGroupId?: string;
  /** Asset / timeline events */
  date?: Date | string;
  /** Revenue / expense rows */
  startDate?: Date | string;
  endDate?: Date | string;
}

export function getSegmentStartDate(item: SegmentLike): Date | undefined {
  return toDate(item.startDate ?? item.date);
}

/** Group key for segment chains; legacy items use their own id. */
export function getSegmentGroupKey(item: SegmentLike): string {
  return item.lineItemGroupId ?? item.id;
}

export function isSegmentActiveInMonth(item: SegmentLike, targetDate: Date): boolean {
  const start = getSegmentStartDate(item);
  return isActiveInMonth({ startDate: start, endDate: toDate(item.endDate) }, targetDate);
}

/**
 * Pick the active segment for a month within one logical line item.
 * When segments overlap, the one with the latest start date wins.
 */
export function resolveActiveSegment<T extends SegmentLike>(
  segments: T[],
  targetMonth: Date,
): T | null {
  const active = segments.filter((segment) => isSegmentActiveInMonth(segment, targetMonth));
  if (active.length === 0) return null;
  if (active.length === 1) return active[0];

  return active.sort((a, b) => {
    const aStart = getSegmentStartDate(a)?.getTime() ?? 0;
    const bStart = getSegmentStartDate(b)?.getTime() ?? 0;
    return bStart - aStart;
  })[0];
}

export function groupSegmentsByLineItem<T extends SegmentLike>(
  items: T[],
): Map<string, T[]> {
  const map = new Map<string, T[]>();
  for (const item of items) {
    const key = getSegmentGroupKey(item);
    const list = map.get(key) ?? [];
    list.push(item);
    map.set(key, list);
  }
  return map;
}

/** Last calendar day of the month before `date` (UTC). */
export function lastDayOfMonthBefore(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 0));
}

function isOneTimeFrequency(frequency?: string): boolean {
  return frequency === 'one-time';
}

function resolveActiveRecurringItems<T extends SegmentLike>(
  items: T[],
  targetMonth: Date,
  isRecurring: (item: T) => boolean,
): T[] {
  const passThrough: T[] = [];
  const recurring: T[] = [];

  for (const item of items) {
    if (isRecurring(item)) {
      recurring.push(item);
      continue;
    }
    if (isSegmentActiveInMonth(item, targetMonth)) {
      passThrough.push(item);
    }
  }

  const groups = groupSegmentsByLineItem(recurring);
  const active: T[] = [...passThrough];

  for (const segments of groups.values()) {
    const segment = resolveActiveSegment(segments, targetMonth);
    if (segment) active.push(segment);
  }

  return active;
}

function isOneTimeRevenue(
  revenue: SegmentLike & { frequency?: string; isRecurring?: boolean },
): boolean {
  return revenue.isRecurring === false || isOneTimeFrequency(revenue.frequency);
}

function isOneTimeExpense(
  expense: SegmentLike & { frequency?: string; isOneTime?: boolean },
): boolean {
  return Boolean(expense.isOneTime) || isOneTimeFrequency(expense.frequency);
}

export function resolveActiveRevenues<
  T extends SegmentLike & { frequency?: string; isRecurring?: boolean; isCustomerDriven?: boolean },
>(revenues: T[], targetMonth: Date): T[] {
  const recurringOnly = revenues.filter((revenue) => !isOneTimeRevenue(revenue));
  return resolveActiveRecurringItems(recurringOnly, targetMonth, () => true);
}

export function resolveActiveExpenses<
  T extends SegmentLike & { frequency?: string; isOneTime?: boolean },
>(expenses: T[], targetMonth: Date): T[] {
  const recurringOnly = expenses.filter((expense) => !isOneTimeExpense(expense));
  return resolveActiveRecurringItems(recurringOnly, targetMonth, () => true);
}
